import { Component, inject } from '@angular/core';
import { PenguinService } from './penguin.service';

/**
 * Renders the easter-egg overlay (two penguins walking + high-five + footprint
 * trail) on top of every page. State lives in `PenguinService` — this
 * component only paints. Mounted once at the App root, fixed/pointer-events
 * tuned so it never blocks normal interactions when idle.
 */
@Component({
  selector: 'cmt-penguin-overlay',
  standalone: true,
  template: `
    @if (penguin.walking()) {
      <!-- Footprint trail — two small ovals (instead of an emoji that renders inconsistently
           across OS) so the trail looks deliberate and reads as actual footprints. -->
      @for (fp of penguin.footprints(); track fp.id) {
        <div class="footprint" [style.left.px]="fp.x" [style.top.px]="fp.y">
          <span></span><span></span>
        </div>
      }

      <!-- Penguin sprite — AMIE-branded easter-egg PNG (transparent BG).
        Two-layer DOM: outer .penguin-walker drives horizontal travel + scaleX flip for the
        bottom variant; inner .penguin-waddle layers a vertical bob + slight rotation so the
        penguin truly waddles instead of sliding. Independent layers prevent the bottom
        penguin's scaleX from inverting the waddle direction.
      -->
      <div
        class="penguin-walker penguin-top"
        [class.walk-paused]="penguin.paused()"
        role="button"
        tabindex="0"
        aria-label="High-five penguin"
        (click)="onTap($event)"
        (keydown.enter)="onTap($any($event))"
      >
        <div class="penguin-flip">
          <div class="penguin-waddle">
            <img src="easter_pengu.png" alt="" draggable="false" />
          </div>
        </div>
      </div>

      <!-- High-five burst — single emoji at the meeting point. -->
      @if (penguin.highFive()) {
        <div class="high-five-burst">🙌</div>
      }

      <!-- Speech bubbles — one per penguin, held ~2s during the meet-up pause.
           AMIE greets upper-right; the other replies lower-left. -->
      @if (penguin.greeting()) {
        <div class="amie-bubble bubble-amie">Hello, I'm AMIE!</div>
        <div class="amie-bubble bubble-meet">Nice to meet you!</div>
      }

      <div
        class="penguin-walker penguin-bottom"
        [class.walk-paused]="penguin.paused()"
        role="button"
        tabindex="0"
        aria-label="High-five penguin"
        (click)="onTap($event)"
        (keydown.enter)="onTap($any($event))"
      >
        <div class="penguin-flip">
          <div class="penguin-waddle">
            <img src="easter_pengu.png" alt="" draggable="false" />
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    :host { display: contents; }

    /*
     * Three layers so the choreography composes cleanly:
     *   .penguin-walker — outer, owns horizontal travel via left/right + the
     *      vertical-approach translateY for the high-five meet-up
     *   .penguin-flip   — middle, only the bottom variant flips here so scaleX
     *      doesn't clash with the outer translateY or inner waddle rotation
     *   .penguin-waddle — inner, owns the body sway + bob
     */
    .penguin-walker {
      position: fixed;
      /* Match the cropped easter_pengu.png aspect (831:972) so the PNG fills
       * the box edge-to-edge — bbox.bottom now equals the visible feet, which
       * keeps the footprint trail anchored at floor level. */
      width: 130px;
      height: 152px;
      z-index: 100;
      pointer-events: auto;
      cursor: pointer;
      filter: drop-shadow(0 6px 14px rgba(20, 18, 30, 0.3));
    }
    .penguin-walker:hover .penguin-waddle,
    .penguin-walker:focus-visible .penguin-waddle {
      animation-play-state: paused;
      transform: scale(1.08) rotate(0deg);
    }
    /* Freeze only the travel — the waddle keeps going so they look alive while posing. */
    .penguin-walker.walk-paused {
      animation-play-state: paused;
    }
    .penguin-walker img { width: 100%; height: 100%; display: block; object-fit: contain; user-select: none; -webkit-user-drag: none; }

    .penguin-flip {
      width: 100%;
      height: 100%;
    }
    .penguin-bottom .penguin-flip {
      transform: scaleX(-1);
    }

    .penguin-waddle {
      width: 100%;
      height: 100%;
      transform-origin: 50% 100%;
      animation: penguin-waddle 0.55s ease-in-out infinite alternate;
      transition: transform 120ms ease;
    }

    /*
     * Choreography (12s total):
     *   0–35%  walk horizontally to viewport center
     *   35–45% pause + slide vertically toward each other (top moves DOWN, bottom moves UP)
     *   45–55% hold at meeting position — high-five burst plays
     *   55–65% slide vertically back to lane
     *   65–100% walk out the other side
     *
     * The vertical shift uses calc() relative to viewport so it adapts to any height.
     * Top penguin needs to come down ~ (50vh - top_offset - half_height); we approximate
     * with "50vh - 200px" which leaves a small visual gap above center for the high-five.
     * Bottom penguin mirrors the move via translateY going negative.
     */
    .penguin-top {
      top: 5rem;
      animation: penguin-walk-ltr 12s cubic-bezier(0.4, 0, 0.6, 1) forwards;
    }
    .penguin-bottom {
      bottom: 4rem;
      animation: penguin-walk-rtl 12s cubic-bezier(0.4, 0, 0.6, 1) forwards;
    }

    @keyframes penguin-walk-ltr {
      0%   { left: -120px;                  transform: translateY(0); }
      35%  { left: calc(50vw - 46px);       transform: translateY(0); }
      45%  { left: calc(50vw - 46px);       transform: translateY(calc(50vh - 200px)); }
      55%  { left: calc(50vw - 46px);       transform: translateY(calc(50vh - 200px)); }
      65%  { left: calc(50vw - 46px);       transform: translateY(0); }
      100% { left: calc(100vw + 20px);      transform: translateY(0); }
    }
    @keyframes penguin-walk-rtl {
      0%   { right: -120px;                 transform: translateY(0); }
      35%  { right: calc(50vw - 46px);      transform: translateY(0); }
      45%  { right: calc(50vw - 46px);      transform: translateY(calc(-50vh + 200px)); }
      55%  { right: calc(50vw - 46px);      transform: translateY(calc(-50vh + 200px)); }
      65%  { right: calc(50vw - 46px);      transform: translateY(0); }
      100% { right: calc(100vw + 20px);     transform: translateY(0); }
    }

    /* Waddle: gentle sway + bob. Pivot at the feet so the body rocks rather than slides. */
    @keyframes penguin-waddle {
      0%   { transform: rotate(-4deg) translateY(0); }
      50%  { transform: rotate(0deg)  translateY(-3px); }
      100% { transform: rotate( 4deg) translateY(0); }
    }

    /* Tap bounce — overlays a vertical hop on the waddle without disturbing the walk. */
    .penguin-walker.penguin-jump .penguin-waddle {
      animation:
        penguin-waddle 0.55s ease-in-out infinite alternate,
        penguin-bounce 380ms ease-out;
    }
    @keyframes penguin-bounce {
      0%, 100% { translate: 0 0; }
      50%      { translate: 0 -22px; }
    }

    /*
     * Footprints — two small ovals that mimic actual penguin feet. The dark navy color
     * + 0.5 opacity reads as "left a mark" without being aggressive on light themes.
     */
    .footprint {
      position: fixed;
      width: 14px;
      height: 18px;
      display: inline-flex;
      gap: 3px;
      pointer-events: none;
      z-index: 99;
      animation: footprint-fade 2.4s ease-out forwards;
    }
    .footprint span {
      width: 5px;
      height: 9px;
      border-radius: 50%;
      /* Theme token instead of a hardcoded penguin-blue so the trail also reads on Dark. */
      background: var(--foreground);
      opacity: 0.8;
    }
    .footprint span:nth-child(2) { margin-top: 4px; }
    @keyframes footprint-fade {
      0%   { opacity: 0.55; transform: scale(0.9); }
      100% { opacity: 0;    transform: scale(1.05); }
    }

    /* Single high-five burst centered between the two penguins at the meeting point. */
    .high-five-burst {
      position: fixed;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%) scale(0.5);
      font-size: 64px;
      z-index: 101;
      pointer-events: none;
      animation: high-five-pop 1.4s ease-out forwards;
    }
    @keyframes high-five-pop {
      0%   { transform: translate(-50%, -50%) scale(0.4) rotate(-12deg); opacity: 0; }
      25%  { transform: translate(-50%, -50%) scale(1.5) rotate(6deg);  opacity: 1; }
      55%  { transform: translate(-50%, -50%) scale(1.1) rotate(-4deg); opacity: 1; }
      100% { transform: translate(-50%, -50%) scale(0.95) rotate(0deg); opacity: 0; }
    }

    /*
     * Greeting bubbles — classic beige comic speech bubbles with a soft drop shadow,
     * one per penguin during the meeting pause. Penguins meet at horizontal center
     * (≈50vw). Beige + dark ink is a deliberate comic look (same on every theme), so
     * these are intentional fixed colors rather than theme tokens. Each tail (::after)
     * points back toward its own penguin.
     */
    .amie-bubble {
      position: fixed;
      padding: 0.5rem 1rem;
      background: #f5ebd8;
      color: #2c2418;
      border: 1px solid #e2d4b6;
      border-radius: 1rem;
      font-family: var(--font-display), serif;
      font-weight: 600;
      font-size: 1.1rem;
      white-space: nowrap;
      box-shadow: 0 12px 30px rgba(18, 16, 28, 0.5), 0 3px 8px rgba(18, 16, 28, 0.35);
      z-index: 102;
      pointer-events: none;
    }
    .amie-bubble::after {
      content: '';
      position: absolute;
      width: 14px;
      height: 14px;
      background: #f5ebd8;
      transform: rotate(45deg);
    }

    /* AMIE bubble — upper-right of the meeting; tail points LEFT toward the penguin. */
    .bubble-amie {
      left: calc(50vw + 115px);
      top: calc(50% - 72px);
      transform-origin: left center;
      animation: bubble-life 2s ease-out forwards;
    }
    .bubble-amie::after {
      left: -7px;
      top: 50%;
      margin-top: -7px;
      border-left: 1px solid #e2d4b6;
      border-bottom: 1px solid #e2d4b6;
    }

    /* Reply bubble — lower-left of the meeting; tail points RIGHT toward the penguin. */
    .bubble-meet {
      right: calc(50vw + 100px);
      top: calc(50% + 78px);
      transform-origin: right center;
      animation: bubble-life 2s ease-out forwards;
    }
    .bubble-meet::after {
      right: -7px;
      top: 50%;
      margin-top: -7px;
      border-right: 1px solid #e2d4b6;
      border-top: 1px solid #e2d4b6;
    }

    /* 2s life: quick pop-in, short hold, gentle fade-out at the end. */
    @keyframes bubble-life {
      0%   { opacity: 0; transform: scale(0.6); }
      4%   { opacity: 1; transform: scale(1.06); }
      8%   { opacity: 1; transform: scale(1); }
      92%  { opacity: 1; transform: scale(1); }
      100% { opacity: 0; transform: scale(0.96); }
    }
  `],
  imports: [],
})
export class PenguinOverlayComponent {
  readonly penguin = inject(PenguinService);

  onTap(event: MouseEvent): void {
    const target = event.currentTarget as HTMLElement;
    target.classList.remove('penguin-jump');
    void target.offsetWidth; // restart animation
    target.classList.add('penguin-jump');
    this.penguin.noot();
  }
}
